import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/useAuthStore';
import logo from '../../assets/logo.png';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navLinks = [
    { label: 'How it works', href: '#how-it-works' },
    { label: 'Features', href: '#features' },
    { label: 'Roadmap', href: '#roadmap' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Contact', href: '/contact', isRoute: true },
  ];

  return (
    <>
      {/* ── Header bar ── */}
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8" aria-label="Global">
          <div className="flex lg:flex-1 items-center">
            <Link to="/">
              <img
                src={logo}
                alt="LocalContact"
                className="h-8 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Hamburger — always on top via z-50, above any overlay */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className={`relative z-50 -m-2.5 inline-flex items-center justify-center rounded-md p-2.5 transition-colors ${scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/10'}`}
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen
                ? <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                : <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              }
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-[13px] font-bold transition-colors relative group ${
                    scrolled ? 'text-slate-500 hover:text-slate-900' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 bg-brand-500 rounded-full transition-all duration-200 group-hover:w-full" />
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className={`text-[13px] font-bold transition-colors relative group ${
                    scrolled ? 'text-slate-500 hover:text-slate-900' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 bg-brand-500 rounded-full transition-all duration-200 group-hover:w-full" />
                </a>
              )
            )}
          </div>

          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-6">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="rounded-full bg-brand-500 px-6 py-2.5 text-[13px] font-bold text-slate-900 shadow-md shadow-brand-500/20 hover:bg-brand-400 transition-all hover:-translate-y-px"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-[13px] font-bold transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`}
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="rounded-full bg-brand-500 px-6 py-2.5 text-[13px] font-bold text-slate-900 shadow-md shadow-brand-500/20 hover:bg-brand-400 transition-all hover:-translate-y-px"
                >
                  Start for free
                </Link>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* ── Mobile menu — rendered OUTSIDE header to avoid stacking-context trap ── */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[9999]" role="dialog" aria-modal="true">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* panel */}
          <div className="absolute inset-y-0 right-0 w-full sm:max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <img src={logo} alt="LocalContact" className="h-8 w-auto object-contain" />
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-slate-600 hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-1 mb-8">
                {navLinks.map((link) =>
                  link.isRoute ? (
                    <Link
                      key={link.label}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center rounded-xl px-4 py-3.5 text-[15px] font-bold text-slate-700 hover:bg-brand-50 hover:text-slate-900 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center rounded-xl px-4 py-3.5 text-[15px] font-bold text-slate-700 hover:bg-brand-50 hover:text-slate-900 transition-colors"
                    >
                      {link.label}
                    </a>
                  )
                )}
              </div>

              <div className="border-t border-slate-100 pt-6 flex flex-col gap-3">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-xl bg-brand-600 px-4 py-3.5 text-center text-[15px] font-bold text-slate-900 hover:bg-brand-700 transition-colors"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block rounded-xl px-4 py-3.5 text-[15px] font-bold text-slate-700 hover:bg-slate-50 text-center border border-slate-200 transition-colors"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block rounded-xl bg-brand-600 px-4 py-3.5 text-center text-[15px] font-bold text-slate-900 hover:bg-brand-700 transition-colors"
                    >
                      Start for free →
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
